import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  CreateOrderReportPayload,
  CreateReportPayload,
  Report,
} from "@web-app/types";

import { NotificationResponse } from "../services/notifications";
import {
  createOrderReport,
  createReport,
  getReportsByOrderId,
  getReportsByOrderIds,
  updateReportResolution,
} from "../services/reports";

export const useCreateReport = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Report,
    Error,
    { orderId: string; payload: CreateReportPayload }
  >({
    mutationFn: ({ orderId, payload }) => createReport(orderId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["order-reports"],
      });
    },
  });
};

export const useCreateOrderReport = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Report,
    Error,
    { orderId: string; payload: CreateOrderReportPayload }
  >({
    mutationFn: ({ orderId, payload }) => createOrderReport(orderId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["order-reports"],
      });
      queryClient.invalidateQueries({
        queryKey: ["batch-reports"],
      });
    },
  });
};

export const useGetReportsByOrderId = (
  orderId: string,
  options?: { enabled?: boolean },
) => {
  return useQuery<Report[], Error>({
    enabled: !!orderId && options?.enabled !== false,
    queryFn: () => getReportsByOrderId(orderId),
    queryKey: ["order-reports", orderId],
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
};

export const useReportsByOrderIds = (
  orderIds: string[],
  options?: { enabled?: boolean },
) => {
  return useQuery<Record<string, Report[]>, Error>({
    enabled: orderIds.length > 0 && options?.enabled !== false,
    queryFn: () => getReportsByOrderIds(orderIds),
    queryKey: ["batch-reports", ...orderIds.sort()],
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
};

export const useOrderReports = (orderId: string) => {
  const queryClient = useQueryClient();

  const batchData = queryClient.getQueryData<Record<string, Report[]>>(
    queryClient
      .getQueryCache()
      .findAll({ queryKey: ["batch-reports"] })
      .find((query) => {
        const queryKey = query.queryKey as string[];
        return queryKey.includes(orderId);
      })?.queryKey || [],
  );

  return batchData?.[orderId] || [];
};

export const useRouteOrdersReports = (orderIds: string[]) => {
  const queryClient = useQueryClient();
  const reports = queryClient.getQueriesData<Record<string, Report[]>>({
    queryKey: ["batch-reports"],
  });

  const hasReportsInRouteOrders = reports.some(([, data]) => {
    if (!data) return false;
    return orderIds.some((id) => data[id]?.length > 0);
  });

  return hasReportsInRouteOrders;
};

export const useUpdateReport = () => {
  const queryClient = useQueryClient();

  return useMutation<Report, Error, { reportId: string; resolved: boolean }>({
    mutationFn: ({ reportId, resolved }) =>
      updateReportResolution(reportId, resolved),
    onSuccess: (updatedReport) => {
      // Update the notifications cache (used by NotificationPanel)
      queryClient.setQueryData<NotificationResponse[]>(
        ["notifications"],
        (oldData) => {
          if (!oldData) return oldData;

          return oldData.map((notification) => {
            if (
              notification.notificationEvent.entity === "ORDER_REPORT" &&
              notification.report?.id === updatedReport.id
            ) {
              return {
                ...notification,
                report: {
                  ...notification.report,
                  resolved: updatedReport.resolved,
                  resolvedAt: updatedReport.resolvedAt
                    ? new Date(updatedReport.resolvedAt)
                    : undefined,
                  resolvedBy:
                    updatedReport.resolved && updatedReport.resolvedBy
                      ? {
                          id: updatedReport.resolvedBy.toString(),
                          name: "You",
                        }
                      : undefined,
                },
              };
            }
            return notification;
          });
        },
      );

      // Update order-reports cache
      queryClient.setQueryData<Report[]>(
        ["order-reports", updatedReport.orderId?.toString()],
        (oldData) => {
          if (!oldData) return oldData;

          return oldData.map((report) => {
            if (report.id === updatedReport.id) {
              return updatedReport;
            }
            return report;
          });
        },
      );
    },
  });
};
